import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Alert } from "@mui/material";
import axiosInstance from "../axiosInstance";
import LOGO_BASE64 from "../assets/data/logoBase64";
import {
  buildReceiptTable,
  receiptTableCss,
  receiptSlipCss,
} from "../utils/receiptTableHtml";

const primaryColor = "#1976d2";
const successColor = "#2e7d32";
const errorColor = "#d32f2f";

const escapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

const formatCurrency = (amount) =>
  Math.round(Number(amount || 0)).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

const formatSignedCurrency = (amount) =>
  `${amount < 0 ? "- " : ""}₹${formatCurrency(Math.abs(amount))}`;

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
    : "N/A";

const buildReceiptHTML = (data) => {
  const {
    transaction,
    customerDetails,
    accountBalanceData,
    employerName,
    calculatedData = {},
    branchDetails,
    employerDetails,
  } = data;

  const sales = transaction.sales || [];
  const trays = transaction.trays || [];
  const paymentAmount = Number(transaction.paymentAmount) || 0;
  const discount = Number(transaction.discount) || 0;
  const damageRefunds =
    Array.isArray(transaction.damageRefunds) &&
    transaction.damageRefunds.length > 0
      ? transaction.damageRefunds
      : transaction.damageRefund?.enabled || transaction.damageRefund?.total
      ? [transaction.damageRefund]
      : [];
  const damageRefundTotal = damageRefunds.reduce(
    (sum, refund) => sum + (Number(refund.total) || 0),
    0
  );

  const getSaleTotal = (sale) => {
    const savedTotal = Number(sale?.total);
    return sale?.total != null && Number.isFinite(savedTotal)
      ? savedTotal
      : (Number(sale?.numberOfItems) || 0) * (Number(sale?.costPerItem) || 0);
  };
  const salesTotal = sales.reduce((sum, sale) => sum + getSaleTotal(sale), 0);
  const currentLabourTotal = transaction.labourCharges?.enabled
    ? Number(transaction.labourCharges.total || 0)
    : 0;
  const trayPaymentTotal = Number(calculatedData.trayPaymentTotal) || 0;
  const totalCredits = paymentAmount + trayPaymentTotal;
  const netSalesTotal = salesTotal + currentLabourTotal - damageRefundTotal - discount;

  const accountBalance =
    accountBalanceData?.totalDueAmount !== undefined
      ? accountBalanceData.totalDueAmount
      : transaction?.accountBalance || 0;
  const openingBalance = Number.isFinite(Number(calculatedData.openingBalance))
    ? Number(calculatedData.openingBalance)
    : accountBalance - (netSalesTotal - totalCredits);
  const subTotal = openingBalance + netSalesTotal;

  const title = employerName || employerDetails?.employerName || "Durga Bhavani Fruits";

  const trayName = (t) => t.trayName || "Tray";
  const receiptTable = buildReceiptTable({
    salesNotes: transaction.salesNotes,
    paymentNotes: transaction.paymentNotes,
    openingBalance,
    sales: sales.map((s) => ({
      name: s.itemName || "Item",
      qty: s.numberOfItems,
      rate: s.costPerItem,
      amount: getSaleTotal(s),
    })),
    loadingCharges:
      currentLabourTotal > 0
        ? {
            numberOfItems: transaction.labourCharges?.numberOfItems,
            costPerItem: transaction.labourCharges?.costPerItem,
            total: currentLabourTotal,
          }
        : null,
    damageRefunds: damageRefunds.map((r) => ({
      itemName: r.itemName,
      numberOfItems: r.numberOfItems,
      costPerItem: r.costPerItem,
      total: Number(r.total) || 0,
    })),
    discount,
    subTotal,
    paymentAmount,
    trays: trays.map((t) => {
      const price = Number(t.costPerTray) || 0;
      return {
        name: trayName(t),
        qty: t.numberOfTrays,
        rate: price,
        amount: t.excludePayment ? 0 : (Number(t.numberOfTrays) || 0) * price,
        excluded: !!t.excludePayment,
      };
    }),
    trayPaymentTotal,
    closingBalance: accountBalance,
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>${escapeHtml(customerDetails?.name)} - ${formatDate(transaction.dateOfTransaction)}</title>
      <style>
        ${receiptSlipCss(primaryColor)}
        ${receiptTableCss(primaryColor, successColor, errorColor)}
      </style>
    </head>
    <body>
      <div class="slip">
        <div class="header">
          <div class="brand">
            <img src="${LOGO_BASE64}" />
            <div>
              <div class="company">${escapeHtml(title)}</div>
              <div class="branch">${escapeHtml(branchDetails?.name || "Transaction Receipt")}</div>
              <div class="proprietor">Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong></div>
            </div>
          </div>
          <div class="hdate">Date<br /><strong>${formatDate(transaction.dateOfTransaction)}</strong></div>
        </div>
        <div class="meta">
          <div class="customer">${escapeHtml(customerDetails?.name)}</div>
          <div class="location">${escapeHtml(customerDetails?.location || customerDetails?.address)}</div>
        </div>
        ${receiptTable}
      </div>
    </body>
    </html>
  `;
};

const ReceiptViewer = () => {
  const { transactionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      try {
        const response = await axiosInstance.get(`/pdf/transaction/${transactionId}`);
        setData(response.data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.statusText ||
            err.message ||
            "Failed to load transaction details"
        );
      } finally {
        setLoading(false);
      }
    };

    if (transactionId) {
      fetchTransactionDetails();
    }
  }, [transactionId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data?.transaction) {
    return (
      <Box p={3}>
        <Alert severity="warning">No transaction data found</Alert>
      </Box>
    );
  }

  return (
    <iframe
      title="Transaction Receipt"
      srcDoc={buildReceiptHTML(data)}
      style={{ width: "100%", height: "100vh", border: 0, display: "block" }}
    />
  );
};

export default ReceiptViewer;
